import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useContext, useState, useEffect } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { AuthContext } from '../Helpers/Contexts/authContext';
import { AppFilterProps, ReactChildren, SelectOptions, ValuesOfAny } from '../interfaces/Common';
import { getFileUrl } from './apiService';
import routes from './routes';
import Parser, { DOMNode, domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import { Typography, useMediaQuery } from '@mui/material';
import KenLoader from '../GlobalComponents/KenLoader';
import storageService from './storageService';

const userDispatch = async (data: any) => {
  switch (data.case) {
    case "ADD":
      if (data?.key) {
        await storageService.setItem(data.key, data.payload);
      }
      break;
    case "GET":
      if (data?.key) {
        return await storageService.getItem(data.key);
      }
      break;
    case "REMOVE":
      if (data?.key) {
        await storageService.removeItem(data.key);
      }
      break;
    case "CLEAR":
      await storageService.clear();
      break;
    default:
      break;
  }
};

export const getCombinedString: (array: Array<string>, seperator?: string) => string = (array: Array<string>, seperator?: string) => {
  let arrayString: string = '';
  let customSeperator = seperator ? seperator : ',';
  array.forEach((items: string, index: number) => {
    if (arrayString === '') {
      arrayString = `${items}${customSeperator} `;
    } else if (index === array.length - 1) {
      arrayString = arrayString + `${items}`;
    } else {
      arrayString = arrayString + `${items}${customSeperator} `;
    }
  });
  return arrayString;
};

export const splitCombinedStringintoArray = (value: string, seperator?: string) => {
  let customSeperator = seperator ? seperator : ',';
  return value.split(customSeperator);
};

export const handleFilterData: (key: string, array: Array<any>) => Array<SelectOptions> = (key: string, array: Array<any>) => {
  const set:any = new Set();
  const handleNull = (data: string) => {
    if (data && data !== null) {
      set.add(data);
    }
  };
  for (let i = 0; i < array?.length; i++) {
    if (array[i]) {
      const category = array[i][key] as string;
      if (category && `${category}`.includes(';')) {
        const categoryArray = category?.split(';');
        for (let j = 0; j < categoryArray.length; j++) {
          handleNull(categoryArray[j]);
        }
      } else {
        handleNull(category);
      }
    }
  }
  return [...set].map((items) => {
    return { label: items, value: items };
  }) as Array<SelectOptions>;
};

export const getYears: (start: number) => Array<SelectOptions> = (start: number) => {
  const array: Array<SelectOptions> = [];
  const date = new Date();
  for (let i = start; i <= date.getFullYear(); i++) {
    array.push({
      label: `${i}`,
      value: `${i}`,
    });
  }
  return array.reverse();
};

export const PublicRoute: React.FunctionComponent<ReactChildren> = ({ children }) => {
  const {
    state: { user, isAuthenticated, isLoading },
  } = useContext(AuthContext);

  // Show loading while authentication state is being determined
  if (isLoading) {
    return <KenLoader />;
  }

  const roles = user?.contactRoles || [];
  const isInvestor = ['Strategic Investor', 'Passive Investor'].some((role) => roles?.includes(role));
  const isJuryMember = roles?.includes('Jury Member');
  const hasAllRoles = isInvestor && isJuryMember;

  if (isAuthenticated) {
    if (isInvestor || hasAllRoles) {
      return <Redirect to={routes.home} />;
    } else if (isJuryMember) {
      return <Redirect to={routes.juryDashboard} />;
    } else {
      // Default redirect for authenticated users without specific roles
      return <Redirect to={routes.home} />;
    }
  }
  return children;
};

export const PrivateRoute: React.FunctionComponent<ReactChildren> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [userRole, setUserRole] = useState<any>(null);
  const [isGuestUser, setIsGuestUser] = useState<boolean>(false);
  const location = useLocation();

  // Define venue booking related routes that guests can access
  const venueBookingRoutes = [
    '/facilities',
    '/meeting rooms',
    '/facilityDetails',
    '/book facility',
    '/access-card-request',
    '/cart',
    '/billing details',
    '/bookings',
    '/transactions',
    '/calendar',
    '/my profile',
    '/membership-onboard',
    '/notifications'
  ];

  const isVenueBookingRoute = venueBookingRoutes.some(route => 
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const role = await userDispatch({ case: "GET", key: "role" });
        
        // Also check preferences for Guest role
        const preferences = await storageService.getItem("preferences");
        const isGuestFromPreferences = preferences && 
          (preferences[0] === 'Guest' || preferences.includes('Guest'));
        
        setUserRole(role);
        
        // Set guest user status
        const isGuest = role?.roleType === "guest" || 
                       role?.roleType === "Guest" || 
                       isGuestFromPreferences;
        setIsGuestUser(isGuest);
        
        if (isGuest) {
          setIsAuthenticated(true);
        } else {
          const user = await userDispatch({ case: "GET", key: "user" });
          if (user?.email || user?.accountId) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        }
      } catch (err) {
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return <KenLoader />;
  }

  if (!isAuthenticated) {
    return <Redirect to={routes.login} />;
  }

  // If user is a guest and trying to access non-venue booking routes, redirect to facilities
  if (isGuestUser && !isVenueBookingRoute) {
    return <Redirect to={routes.facilities} />;
  }

  return children;
};

export const AppFilterFunction: (props: AppFilterProps) => Array<any> = (props) => {
  const { filterData, data } = props;
  if (Object.keys(filterData).length === 0) {
    return data;
  }

  const filteringData = { ...filterData };
  let sampleEventData = [...data];
  for (const i in filteringData) {
    const filteredArray = [];
    for (let j = 0; j < sampleEventData.length; j++) {
      const element = sampleEventData[j];
      if (element[i] !== null) {
        if (`${element[i]}`.toLowerCase().includes(`${filteringData[i]}`.toLowerCase())) {
          filteredArray.push(element);
        }
      }
    }
    sampleEventData = [...filteredArray];
  }
  return sampleEventData;
};

export function pad(n: string, width: number, z?: string) {
  z = z || '0';
  n = n + '';
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}

export const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>, files: any, types?: Array<string>, size?: number) => {
  // File Check
  if (!e.target.files) {
    return { message: 'Please Upload File', success: false };
  }
  const getFile = e.target.files[0];
  const customSize = size ? size : 2000000;

  // File Type Check
  if (types) {
    const getMatch: Array<string> = types?.filter((items) => {
      return items === getFile.type;
    });
    if (getMatch.length === 0) {
      return {
        message: `Only the ${types.map((items) => {
          return `${items} `;
        })} formats are accepted`,
        success: false,
      };
    }
  }

  // File Size Check
  if (getFile.size > customSize) {
    return { message: 'File is larger than 1MB', success: false };
  }
  try {
    const data = await getFileUrl(e.target.files[0]);
    if (data.result?.success) {
      return data;
    } else {
      return { message: 'Upload Failed, Try Again !', success: false };
    }
  } catch (err) {
    return { message: 'Upload Failed, Try Again !', success: false };
  }
};

export const moneyValidator = (start: string, end: string) => {
  const salary = start === end;
  return salary ? `${start} LPA` : `${start}-${end} LPA`;
};

export const rangeValidatorPayload = (year: string) => {
  const getYears = year.split('-');
  if (getYears.length === 2) {
    return [parseInt(getYears[0]), parseInt(getYears[1])];
  } else if (getYears.length === 1) {
    return [parseInt(getYears[0]), parseInt(getYears[0])];
  } else {
    return '';
  }
};

export const uniqueArrayCreator = (array: Array<any>, key: string) => {
  return Array.from(
    new Set([
      ...array?.map((items: any) => {
        return items[key];
      }),
    ])
  );
};

export const registeredDatas = (array: Array<any>, objectKey: string) => {
  const newObject: ValuesOfAny = {};
  array?.forEach((items: any) => {
    const keyValue = items[objectKey];
    const date = moment(keyValue).format('Do MMMM YYYY');
    items['find'] = date;
    if (newObject.hasOwnProperty(date)) {
      newObject[date].push({ ...items });
    } else {
      newObject[`${date}`] = [items];
    }
  });
  return newObject;
};

export const copyFunction = (text: string) => {
  const clipboardItem = new ClipboardItem({ 'text/plain': new Blob([text], { type: 'text/plain' }) });
  navigator.clipboard
    .write([clipboardItem])
    .then(() => {
      enqueueSnackbar({ message: 'Link copied', variant: 'success' });
    })
    .catch((err) => {
      console.error(err);
    });
};

export const removeHTMLTags = (htmlString: string) => {
  return htmlString.replace(/<[^>]*>/g, '');
};

export const getUsernameFromURL = (url: string) => {
  try {
    const { hostname, pathname } = new URL(url);
    if (hostname.includes('linkedin')) {
      return pathname.split('/')[2];
    } else {
      const domainParts = hostname.split('.');
      const domain = domainParts.slice(-1).join('.');
      return `${domainParts[0]}.${domain}`;
    }
  } catch (error) {
    console.error('Invalid URL', error);
    return '';
  }
};

export const formatAmount = (amount: number) => {
  if (amount >= 1_000_000) {
    return (amount / 1_000_000).toFixed(1) + 'M';
  } else if (amount >= 1_000) {
    return (amount / 1_000).toFixed(1) + 'K';
  }
  return amount;
};

export const truncateText = (text: string | null | undefined, maxLength: number) => {
  if (!text) {
    return ''; // return an empty string if text is null or undefined
  }

  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
};

export const formatFundSize = (size: any, fundingProgram: string | undefined): string => {
  const numSize = parseFloat(size);

  if (isNaN(numSize)) {
    return 'Invalid number';
  }

  // Check if fundingProgram is defined before using it
  const isIndiaProgram = fundingProgram?.toLowerCase().startsWith('india') || false;

  let formattedValue: number;
  let unit: string;

  if (isIndiaProgram) {
    // Format in Thousands, Lakhs, Crores, etc.
    if (numSize >= 1e7) {
      formattedValue = Math.round((numSize / 1e7) * 100) / 100; // Crores with 2 decimals
      unit = 'Cr';
    } else if (numSize >= 1e5) {
      formattedValue = Math.round((numSize / 1e5) * 100) / 100; // Lakhs with 2 decimals
      unit = 'L';
    } else if (numSize >= 1e3) {
      formattedValue = Math.round((numSize / 1e3) * 100) / 100; // Thousands with 2 decimals
      unit = 'K';
    } else {
      formattedValue = Math.round(numSize * 100) / 100; // No unit with 2 decimals
      unit = '';
    }
  } else {
    // Format in Thousands, Millions, Billions, etc.
    if (numSize >= 1e12) {
      formattedValue = Math.round((numSize / 1e12) * 100) / 100; // Trillions with 2 decimals
      unit = 'T';
    } else if (numSize >= 1e9) {
      formattedValue = Math.round((numSize / 1e9) * 100) / 100; // Billions with 2 decimals
      unit = 'B';
    } else if (numSize >= 1e6) {
      formattedValue = Math.round((numSize / 1e6) * 100) / 100; // Millions with 2 decimals
      unit = 'M';
    } else if (numSize >= 1e3) {
      formattedValue = Math.round((numSize / 1e3) * 100) / 100; // Thousands with 2 decimals
      unit = 'K';
    } else {
      formattedValue = Math.round(numSize * 100) / 100; // No unit with 2 decimals
      unit = '';
    }
  }

  return `${formattedValue}${unit}`.trim();
};

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '00-00-00';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '00-00-00';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-4);
  return `${day}-${month}-${year}`;
};

export const formatDate2 = (dateString: any) => {
  const date = new Date(dateString);
  const options: any = { day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

export const stripHtmlTags = (str: string) => {
  return str.replace(/<\/?[^>]+(>|$)/g, '');
};

// Function to parse HTML content to plain text for deals data
export const parseHtmlToText = (htmlString: string | null | undefined): string => {
  if (!htmlString) return '';
  
  // Convert HTML to plain text
  let text = htmlString
    // Convert <li> tags to bullet points
    .replace(/<li>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    // Convert <ul> and </ul> tags
    .replace(/<\/?ul>/gi, '')
    // Convert <p> tags to line breaks
    .replace(/<\/p>/gi, '\n')
    .replace(/<p[^>]*>/gi, '')
    // Convert <br> tags to line breaks
    .replace(/<br\s*\/?>/gi, '\n')
    // Convert span tags with styles to plain text
    .replace(/<span[^>]*>/gi, '')
    .replace(/<\/span>/gi, '')
    // Remove any remaining HTML tags
    .replace(/<[^>]*>/g, '')
    // Decode HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Clean up extra whitespace and line breaks
    .replace(/\n\s*\n/g, '\n')
    .replace(/^\s+|\s+$/g, '')
    // Remove excessive spaces
    .replace(/\s+/g, ' ');
  
  return text;
};

export const formatCountryOfRegistration = (countryOfRegistration: string): string => {
  if (!countryOfRegistration) {
    return '';
  }

  const countries = countryOfRegistration.split(';');

  if (countries.length > 1) {
    return `${countries[0]}+${countries.length - 1}`;
  }

  return countries[0] || '';
};

export const extractListItems = (html: string): string[] => {
  const matches = html?.match(/<li>(.*?)<\/li>/g);
  return matches ? matches.map((item) => item.replace(/<\/?li>/g, '')) : [];
};

export const customParser = (htmlString: string): React.ReactNode => {
  const options: HTMLReactParserOptions = {
    replace: (domNode:any) => {
      // Ensure the node is an element node with attributes
      if (domNode instanceof Element && domNode.attribs) {
        if (domNode.name === 'p') {
          return <Typography sx={{ fontSize: { xs: '12px', ultraHD: '16px' }, color: '#333' }}>{domToReact(domNode.children as DOMNode[], options)}</Typography>;
        }

        // Add conditions for other HTML elements as needed
        if (domNode.name === 'span') {
          return <Typography sx={{ fontSize: { xs: '12px', ultraHD: '16px' }, color: '#666' }}>{domToReact(domNode.children as DOMNode[], options)}</Typography>;
        }
      }
    },
  };

  return Parser(htmlString, options);
};
export const useIsSmallMobile = () => {
  return useMediaQuery('(max-width:450px)');
};

export const useIsMobile = () => {
  return useMediaQuery('(max-width:700px)');
};

export const useIsMediumScreen = () => {
  return useMediaQuery('(max-width:1450px)');
};

export const formatWebsiteUrl = (website: string): string => {
  if (!website) return ''; // Return an empty string if the website is null or undefined
  return website.startsWith('https://') ? website : `https://` + website;
};

// Helper function to calculate completion percentage
export const calculatePercentage = (currentValue: string | number, targetValue: string | number): number => {
  const current = parseFloat(currentValue as string) || 0;
  const target = parseFloat(targetValue as string) || 0;
  if (isNaN(current) || isNaN(target) || target === 0) {
    return 0;
  }
  return Math.round((current / target) * 100);
};

// events
export const splitBase64 = (base64: string) => {
  const splitData = base64.split(',');
  return splitData[1];
};
export function removeNullKeys(obj: any) {
  for (let key in obj) {
    if (obj[key] === null) {
      delete obj[key];
    } else if (typeof obj[key] === 'object') {
      removeNullKeys(obj[key]);
      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      }
    }
  }
  return obj;
}

export function checkMilestoneStatus(mileStoneEndDate: any) {
  const today = new Date();
  const endDate = new Date(mileStoneEndDate);

  if (endDate > today) {
    return 'On Track';
  } else {
    return 'Delayed';
  }
}
